/**
 * ADP-211 — run-level strategy-application impact read model (ADP-199).
 *
 * Answers "how did the campaign perform before vs after this AI run's
 * executed changes were applied?" at campaign_daily_metrics grain. Pure
 * read-model DTO aggregated at query time, never persisted.
 *
 * Semantics shared with the ADP-60 action-level model: the execution day is
 * excluded from both windows, ratio metrics are derived from window sums
 * (null on zero denominators), and `delta`/`changeRatios` are only non-null
 * when dataStatus is "ready" so incomplete windows never imply a verdict.
 */
import { z } from "zod";

const isoDateTimeSchema = z.string().datetime({ offset: true });
const uuidSchema = z.string().uuid();
const calendarDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const nullableMoneySchema = z.number().finite().nonnegative().nullable();

export const RUN_IMPACT_WINDOW_DAYS = [7, 14, 30] as const;
export const RunImpactWindowDaysSchema = z.union([z.literal(7), z.literal(14), z.literal(30)]);
export type RunImpactWindowDays = z.infer<typeof RunImpactWindowDaysSchema>;

export const RunImpactDataStatusSchema = z.enum([
  "ready",
  "waiting_for_after_window",
  "insufficient_metrics",
]);
export type RunImpactDataStatus = z.infer<typeof RunImpactDataStatusSchema>;

export const RunImpactReasonSchema = z.enum([
  "after_window_incomplete",
  "no_before_metrics",
  "no_after_metrics",
]);
export type RunImpactReason = z.infer<typeof RunImpactReasonSchema>;

/** Window totals: raw sums plus ratios derived from those sums. */
export const RunImpactMetricsSchema = z.object({
  impressions: z.number().int().nonnegative(),
  clicks: z.number().int().nonnegative(),
  spend: z.number().finite().nonnegative(),
  sales: z.number().finite().nonnegative(),
  orders: z.number().int().nonnegative(),
  ctr: z.number().finite().nonnegative().nullable(),
  cvr: z.number().finite().nonnegative().nullable(),
  cpc: z.number().finite().nonnegative().nullable(),
  acos: z.number().finite().nonnegative().nullable(),
  roas: z.number().finite().nonnegative().nullable(),
});
export type RunImpactMetrics = z.infer<typeof RunImpactMetricsSchema>;

export const RunImpactWindowSchema = z.object({
  startDate: calendarDateSchema,
  endDate: calendarDateSchema,
  daysCovered: z.number().int().nonnegative(),
  metrics: RunImpactMetricsSchema,
});
export type RunImpactWindow = z.infer<typeof RunImpactWindowSchema>;

/** after − before; ratio entries are percentage-point diffs, null when either side is null. */
export const RunImpactDeltaSchema = z.object({
  impressions: z.number().int(),
  clicks: z.number().int(),
  spend: z.number().finite(),
  sales: z.number().finite(),
  orders: z.number().int(),
  ctr: z.number().finite().nullable(),
  cvr: z.number().finite().nullable(),
  cpc: z.number().finite().nullable(),
  acos: z.number().finite().nullable(),
  roas: z.number().finite().nullable(),
});
export type RunImpactDelta = z.infer<typeof RunImpactDeltaSchema>;

/**
 * Relative change (after − before) / before for volume metrics only — a
 * relative change of a ratio reads ambiguously, and a zero baseline yields
 * null instead of a meaningless percentage.
 */
export const RunImpactChangeRatiosSchema = z.object({
  impressions: z.number().finite().nullable(),
  clicks: z.number().finite().nullable(),
  spend: z.number().finite().nullable(),
  sales: z.number().finite().nullable(),
  orders: z.number().finite().nullable(),
});
export type RunImpactChangeRatios = z.infer<typeof RunImpactChangeRatiosSchema>;

/**
 * The strategy in force when the run's actions were validated, read from the
 * immutable policy snapshot of the executed actions (not the group's current
 * config, which may have changed since). Policy fields are null when the
 * snapshot is missing or predates the versioned policy contract.
 */
export const RunImpactStrategySchema = z.object({
  source: z.enum(["managed_group", "campaign"]),
  managedGroupId: uuidSchema.nullable(),
  managedGroupName: z.string().nullable(),
  businessGoal: z.string().min(1).nullable(),
  targetAcos: z.number().finite().positive().nullable(),
  riskPreference: z.string().min(1).nullable(),
  executionMode: z.enum(["recommendation", "semi-auto", "full-auto"]).nullable(),
});
export type RunImpactStrategy = z.infer<typeof RunImpactStrategySchema>;

export const RunImpactExecutedChangeSchema = z.object({
  aiActionId: uuidSchema,
  bidChangeEventId: uuidSchema,
  targetId: uuidSchema.nullable(),
  /** Keyword text or auto-target expression type; null when the target row is gone. */
  targetLabel: z.string().nullable(),
  keywordMatchType: z.string().nullable(),
  actionType: z.string().min(1),
  oldBid: nullableMoneySchema,
  newBid: nullableMoneySchema,
  changePercentage: z.number().finite().nullable(),
  executedAt: isoDateTimeSchema,
  /** True for human-triggered rollback compensations (ai_actions.rollback_of_action_id). */
  isRollback: z.boolean(),
});
export type RunImpactExecutedChange = z.infer<typeof RunImpactExecutedChangeSchema>;

/** One day of campaign metrics; null metric values mean no reported row for that day. */
export const RunImpactTrendPointSchema = z.object({
  date: calendarDateSchema,
  phase: z.enum(["before", "execution", "after"]),
  impressions: z.number().int().nonnegative().nullable(),
  clicks: z.number().int().nonnegative().nullable(),
  spend: z.number().finite().nonnegative().nullable(),
  sales: z.number().finite().nonnegative().nullable(),
  orders: z.number().int().nonnegative().nullable(),
  ctr: z.number().finite().nonnegative().nullable(),
  cvr: z.number().finite().nonnegative().nullable(),
  cpc: z.number().finite().nonnegative().nullable(),
  acos: z.number().finite().nonnegative().nullable(),
  roas: z.number().finite().nonnegative().nullable(),
});
export type RunImpactTrendPoint = z.infer<typeof RunImpactTrendPointSchema>;

/**
 * AdPraxis-recorded changes that overlap the observation windows and may
 * contaminate the comparison: executed events from other runs on the same
 * campaign, and this run's rollback compensations (bid state persists, so
 * even a pivot-day reversal skews the after window). Manual edits made
 * directly in Amazon are not recorded and cannot be detected.
 */
export const RunImpactOverlapSchema = z.object({
  type: z.enum(["other_run", "rollback"]),
  runId: uuidSchema,
  date: calendarDateSchema,
  executedChanges: z.number().int().positive(),
});
export type RunImpactOverlap = z.infer<typeof RunImpactOverlapSchema>;

export const RunImpactStatusDetailSchema = z.object({
  expectedDays: z.number().int().positive(),
  afterWindowEndDate: calendarDateSchema,
  reasons: z.array(RunImpactReasonSchema),
});
export type RunImpactStatusDetail = z.infer<typeof RunImpactStatusDetailSchema>;

export const RunImpactV1Schema = z.object({
  schemaVersion: z.literal(1),
  runId: uuidSchema,
  shopId: uuidSchema,
  campaignId: uuidSchema,
  campaignName: z.string().nullable(),
  jobType: z.string().min(1),
  currencyCode: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .nullable(),
  strategy: RunImpactStrategySchema,
  /** Earliest executed bid_change_event of the run — the application pivot. */
  executedAt: isoDateTimeSchema,
  /**
   * Calendar dates the strategy was applied (semi-auto approvals may execute
   * later than the pivot). Rollback compensations are excluded — they appear
   * in executedChanges/overlaps instead.
   */
  executionDates: z.array(calendarDateSchema).min(1),
  executedChanges: z.array(RunImpactExecutedChangeSchema).min(1),
  windowDays: RunImpactWindowDaysSchema,
  before: RunImpactWindowSchema,
  after: RunImpactWindowSchema,
  delta: RunImpactDeltaSchema.nullable(),
  changeRatios: RunImpactChangeRatiosSchema.nullable(),
  trend: z.array(RunImpactTrendPointSchema),
  overlaps: z.array(RunImpactOverlapSchema),
  dataStatus: RunImpactDataStatusSchema,
  statusDetail: RunImpactStatusDetailSchema,
  /** Last campaign-report date successfully synced within the observation span; null when none. */
  dataThrough: calendarDateSchema.nullable(),
});
export type RunImpactV1 = z.infer<typeof RunImpactV1Schema>;
